import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, UserPlus, Sparkles, Code, Image, GitBranch, Brain, Github, Shield, X, Users, Globe, Infinity, Download, Monitor, Smartphone, BookOpen } from 'lucide-react';
import Avatar from './Avatar';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../context/LanguageContext';
import UserGuideModal from './UserGuideModal';
import VoiceInfoBlock from './blocks/VoiceInfoBlock';
import useEnableBodyScroll from '../hooks/useEnableBodyScroll';
import TransparencyModal from './TransparencyModal';
import '../styles/PricingTransparency.css';

const WelcomeScreen = () => {
  const navigate = useNavigate();
  const [showRights, setShowRights] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  // Modales de Transparencia (CUADRO NEGRO feb-2026 — replicado del flujo
  // de /planes-y-beneficios para que también aparezcan en la landing pre-login).
  const [showCanModal, setShowCanModal] = useState(false);
  const [showCannotModal, setShowCannotModal] = useState(false);
  const { t, language } = useLanguage();
  const es = language === 'es';

  // 🟥 CUADRO NEGRO (2026-05-30) — Efecto de título intercalado.
  // Alterna cada 4s entre `welcome.titleAlt1` y `welcome.titleAlt2`, con un
  // fade suave de 400ms (no invasivo). Cleanup garantizado en unmount.
  const [titleIndex, setTitleIndex] = useState(0);
  const [titleVisible, setTitleVisible] = useState(true);
  useEffect(() => {
    const id = setInterval(() => {
      setTitleVisible(false);
      setTimeout(() => {
        setTitleIndex((i) => (i === 0 ? 1 : 0));
        setTitleVisible(true);
      }, 400);
    }, 4000);
    return () => clearInterval(id);
  }, []);
  const currentTitle = titleIndex === 0
    ? t('welcome.titleAlt1')
    : t('welcome.titleAlt2');

  // Detectar si la app ya está instalada (PWA)
  useEffect(() => {
    // Verificar múltiples indicadores de que la app está instalada
    const checkIfInstalled = () => {
      // 1. Display mode standalone (funciona en Chrome, Edge, etc.)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      
      // 2. iOS Safari standalone mode
      const isIOSStandalone = window.navigator.standalone === true;
      
      // 3. Android TWA (Trusted Web Activity)
      const isTWA = document.referrer.includes('android-app://');
      
      // 4. Check if running from installed app URL
      const isInstalledPWA = window.matchMedia('(display-mode: window-controls-overlay)').matches ||
                             window.matchMedia('(display-mode: minimal-ui)').matches ||
                             window.matchMedia('(display-mode: fullscreen)').matches;
      
      return isStandalone || isIOSStandalone || isTWA || isInstalledPWA;
    };
    
    if (checkIfInstalled()) {
      setIsInstalled(true);
    }
    
    // Escuchar el evento de instalación
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Detectar cuando se instala
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
      setShowInstallModal(false);
    };
    
    window.addEventListener('appinstalled', handleAppInstalled);
    
    // Escuchar cambios en display-mode (por si el usuario instala mientras está en la página)
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (e) => {
      if (e.matches) {
        setIsInstalled(true);
      }
    };
    mediaQuery.addEventListener('change', handleDisplayModeChange);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
      mediaQuery.removeEventListener('change', handleDisplayModeChange);
    };
  }, []);

  // Permitir scroll natural del body solo en esta pagina (el resto de la app
  // usa overflow:hidden para chats con scroll interno). Restaurar al desmontar.
  useEnableBodyScroll();

  // Función para instalar la app
  const handleInstallClick = async () => {
    if (installPrompt) {
      // Si hay prompt nativo, usarlo
      try {
        await installPrompt.prompt();
        const result = await installPrompt.userChoice;
        if (result.outcome === 'accepted') {
          setIsInstalled(true);
        }
        setInstallPrompt(null);
      } catch (err) {
        console.error('Error installing:', err);
        setShowInstallModal(true);
      }
    } else {
      // Mostrar modal con instrucciones
      setShowInstallModal(true);
    }
  };

  const features = [
    { icon: Code, titleKey: 'features.codeGen', descKey: 'features.codeGenDesc' },
    { icon: Image, titleKey: 'features.imageGen', descKey: 'features.imageGenDesc' },
    { icon: GitBranch, titleKey: 'features.diagrams', descKey: 'features.diagramsDesc' },
    { icon: Brain, titleKey: 'features.memory', descKey: 'features.memoryDesc' },
    { icon: Github, titleKey: 'features.github', descKey: 'features.githubDesc' },
  ];

  return (
    <div 
      className="bg-zinc-950 flex flex-col items-center p-6 relative w-full h-auto min-h-fit"
      style={{ paddingBottom: 'calc(140px + env(safe-area-inset-bottom))' }}
      data-testid="welcome-root"
    >
      <div className="flex flex-col items-center w-full pt-5">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>

      {/* Logo and Title */}
      <div className="text-center mb-6">
        <div className="flex justify-center mb-4">
          <Avatar size="xl" />
        </div>
        <h1
          className={`text-4xl font-outfit font-bold text-white mb-2 transition-opacity duration-[400ms] ${titleVisible ? 'opacity-100' : 'opacity-0'}`}
          aria-live="polite"
          data-testid="welcome-title-alternating"
        >
          {currentTitle}
        </h1>
        <p className="text-lg text-zinc-400">
          {t('welcome.subtitle')}
        </p>
      </div>

      {/* Action Buttons - Ahora primero */}
      <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md mb-4">
        <button
          onClick={() => navigate('/login')}
          className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-indigo-500/25"
          data-testid="login-welcome-btn"
        >
          <LogIn className="w-5 h-5" />
          {t('welcome.login')}
        </button>
        <button
          onClick={() => navigate('/login?register=true')}
          className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold rounded-xl transition-all border border-zinc-700"
          data-testid="register-welcome-btn"
        >
          <UserPlus className="w-5 h-5" />
          {t('welcome.register')}
        </button>
      </div>

      {/* Voice CTA removido por solicitud del usuario — el acceso a voz
          queda disponible desde el microfono del Chat y desde el bloque
          informativo "Comunicacion verbal con DamyxIA". */}

      {/* Install App Button */}
      {!isInstalled && (
        <button
          onClick={handleInstallClick}
          className="flex items-center justify-center gap-2 px-4 py-2 mb-8 bg-emerald-400 hover:bg-emerald-300 text-zinc-900 font-medium text-sm rounded-lg transition-all"
          data-testid="install-app-btn"
        >
          <Download className="w-4 h-4" />
          <span>{t('welcome.installApp')}</span>
        </button>
      )}
      
      {/* CUADRO NEGRO: removido banner verde "App Installed" — la operación fue
          exitosa y la UI continúa silenciosamente. */}

      {/* Features Grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6 max-w-3xl">
        {features.map((feature, idx) => (
          <div 
            key={idx}
            className="flex flex-col items-center p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl hover:border-indigo-500/50 transition-colors"
          >
            <feature.icon className="w-8 h-8 text-indigo-400 mb-2" />
            <h3 className="text-sm font-medium text-white text-center">{t(feature.titleKey)}</h3>
            <p className="text-xs text-zinc-500 text-center mt-1">{t(feature.descKey)}</p>
          </div>
        ))}
      </div>

      {/* Botones de Transparencia (par centrado) — FUERA y SOBRE la grilla de planes */}
      <div
        className="w-full max-w-3xl mb-4 flex flex-wrap justify-center items-center gap-3"
        data-testid="welcome-transparency-buttons"
      >
        <button
          type="button"
          className="transparency-button transparency-button--green"
          onClick={() => setShowCanModal(true)}
          data-testid="welcome-transparency-can-btn"
        >
          {es ? 'Transparencia: Qué Puede DamyxIA' : 'Transparency: What DamyxIA Can Do'}
        </button>
        <button
          type="button"
          className="transparency-button transparency-button--brick"
          onClick={() => setShowCannotModal(true)}
          data-testid="welcome-transparency-cannot-btn"
        >
          {es ? 'Transparencia: Qué NO Puede DamyxIA' : 'Transparency: What DamyxIA Cannot Do'}
        </button>
      </div>

      {/* Planes y Papelera (Spec oficial DamyxIA) */}
      <div className="w-full max-w-3xl mb-6 grid md:grid-cols-2 gap-4" data-testid="welcome-planes-grid">
        {/* Plan Free */}
        <div className="p-5 rounded-xl bg-zinc-900/60 border border-zinc-800">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="text-base font-bold text-white">{language === 'es' ? 'Plan Free' : 'Free Plan'}</h3>
            <span className="text-2xl font-bold text-zinc-300">$0</span>
          </div>
          <ul className="space-y-1.5 text-xs text-zinc-300">
            {(language === 'es' ? [
              'Cuota mensual gratuita de imágenes (no acumulable).',
              'Papelera visible pero bloqueada.',
              'Recuperacion individual: $0.50 por imagen o archivo.',
              'Papelera Semanal: $3 por 7 dias de acceso ilimitado.',
              'Retencion de 30 dias.',
              'Acceso limitado al Historial.',
              'Sin thumbnails en Papelera.',
            ] : [
              'Free monthly image quota (non-cumulative).',
              'Trash visible but locked.',
              'Individual recovery: $0.50 per image or file.',
              'Weekly Trash: $3 for 7 days of unlimited access.',
              '30-day retention.',
              'Limited History access.',
              'No thumbnails in Trash.',
            ]).map((item, i) => <li key={i}>• {item}</li>)}
          </ul>
        </div>
        {/* Plan Premium */}
        <div className="p-5 rounded-xl bg-gradient-to-b from-amber-500/10 to-zinc-900/60 border border-amber-500/30">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="text-base font-bold text-white">{language === 'es' ? 'Plan Premium' : 'Premium Plan'}</h3>
            <span className="text-2xl font-bold text-amber-300">$9<span className="text-xs text-amber-400/70">{language === 'es' ? '/mes' : '/mo'}</span></span>
          </div>
          <ul className="space-y-1.5 text-xs text-zinc-200">
            {(language === 'es' ? [
              'Acceso completo a la Papelera.',
              'Recuperacion ilimitada sin costo.',
              'Retencion de 30 dias.',
              'Acceso completo al Historial.',
              'Sin limites de uso.',
              'Sin costos adicionales.',
            ] : [
              'Full Trash access.',
              'Unlimited recovery at no cost.',
              '30-day retention.',
              'Full History access.',
              'No usage limits.',
              'No additional costs.',
            ]).map((item, i) => <li key={i}>• {item}</li>)}
          </ul>
        </div>
      </div>

      {/* Bloque informativo oficial de comunicacion verbal — ubicado entre
          la grilla de planes y el recuadro de Papelera, conforme al layout
          oficial solicitado. */}
      <div className="w-full max-w-3xl mb-6" data-testid="welcome-voice-info">
        <VoiceInfoBlock
          blocked={true}
          onUpgrade={() => {
            try {
              window.dispatchEvent(
                new CustomEvent('damyxia:funcion-premium', {
                  detail: { language, variant: 'voice' },
                })
              );
            } catch { /* ignore */ }
          }}
        />
      </div>

      {/* Como funciona la Papelera + Pagos */}
      <div className="w-full max-w-3xl mb-6 p-5 rounded-xl bg-zinc-900/60 border border-zinc-800" data-testid="welcome-trash-info">
        <h3 className="text-sm font-bold text-white mb-2">{language === 'es' ? 'Papelera' : 'Trash'}</h3>
        <ul className="text-xs text-zinc-300 space-y-1 mb-3">
          {(language === 'es' ? [
            'Todo lo que borres va a la Papelera.',
            'Free: debe pagar para recuperar.',
            'Premium: recuperacion ilimitada.',
          ] : [
            'Everything you delete goes to the Trash.',
            'Free: must pay to recover.',
            'Premium: unlimited recovery.',
          ]).map((item, i) => <li key={i}>• {item}</li>)}
        </ul>
        <h3 className="text-sm font-bold text-white mb-2 mt-3">{language === 'es' ? 'Pagos' : 'Payments'}</h3>
        <ul className="text-xs text-zinc-300 space-y-1">
          {(language === 'es' ? [
            'Pagos seguros con Stripe Checkout.',
            'Opciones: $0.50 por item o $3 por semana ilimitada.',
          ] : [
            'Secure payments via Stripe Checkout.',
            'Options: $0.50 per item or $3 for unlimited week.',
          ]).map((item, i) => <li key={i}>• {item}</li>)}
        </ul>
      </div>

      {/* For Whom Section - Más pequeño y al final */}
      <div className="w-full max-w-xl mb-6 p-4 bg-gradient-to-r from-indigo-900/20 to-purple-900/20 border border-indigo-500/20 rounded-xl">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-indigo-400" />
          <h2 className="text-sm font-semibold text-white">{t('welcome.forWhomTitle')}</h2>
        </div>
        <p className="text-xs text-zinc-400 mb-2">
          {t('welcome.forWhomDesc')}
        </p>
        <div className="flex flex-wrap gap-2">
          <div className="flex items-center gap-1.5 px-2 py-1 bg-zinc-800/50 rounded-md">
            <Globe className="w-3 h-3 text-green-400" />
            <span className="text-xs text-zinc-400">{t('welcome.forWhomFeature1')}</span>
          </div>
          <div className="flex items-center gap-1.5 px-2 py-1 bg-zinc-800/50 rounded-md">
            <Infinity className="w-3 h-3 text-purple-400" />
            <span className="text-xs text-zinc-400">{t('welcome.forWhomFeature2')}</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 flex flex-col items-center gap-3">
        <p className="text-xs text-zinc-600">
          <Sparkles className="w-3 h-3 inline mr-1" />
          {t('welcome.poweredBy')}
        </p>
        
        {/* Botón Guía de Uso - Prominente */}
        <button
          onClick={() => {
            setShowGuide(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors"
          data-testid="user-guide-btn"
        >
          <BookOpen className="w-4 h-4" />
          {language === 'es' ? 'Guía de Uso' : 'User Guide'}
        </button>
        
        <button
          onClick={() => setShowRights(true)}
          className="text-xs text-zinc-500 hover:text-indigo-400 transition-colors flex items-center gap-1"
          data-testid="user-rights-btn"
        >
          <Shield className="w-3 h-3" />
          {t('welcome.userRights')}
        </button>
        <div className="footer-legal">
          <a href="/terminos">{language === 'es' ? 'Términos y Condiciones' : 'Terms and Conditions'}</a>
          <span> • </span>
          <a href="/privacidad">{language === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}</a>
        </div>
        <p className="text-[10px] text-zinc-700 mt-2" data-testid="welcome-build-tag">
          build: 2026-05-03-planes-fix
        </p>
      </div>

      {/* User Guide Modal */}
      <UserGuideModal
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
        language={language}
      />

      {/* User Rights Modal */}
      {showRights && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-2xl bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-zinc-900/80">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-500/20 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-indigo-400" />
                </div>
                <h2 className="text-xl font-semibold text-white">{t('terms.title')}</h2>
              </div>
              <button
                onClick={() => setShowRights(false)}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                data-testid="close-rights-modal"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="space-y-6">
                {/* Propiedad Intelectual */}
                <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-xl p-5">
                  <h3 className="text-lg font-semibold text-indigo-400 mb-3 flex items-center gap-2">
                    <span className="text-2xl">🎨</span>
                    {t('terms.intellectualProperty')}
                  </h3>
                  <p className="text-zinc-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('terms.intellectualPropertyText') }} />
                </div>

                {/* Libertad de Uso */}
                <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-5">
                  <h3 className="text-lg font-semibold text-green-400 mb-3 flex items-center gap-2">
                    <span className="text-2xl">✨</span>
                    {t('terms.totalFreedom')}
                  </h3>
                  <p className="text-zinc-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('terms.totalFreedomText') }} />
                </div>

                {/* Responsabilidad */}
                <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-5">
                  <h3 className="text-lg font-semibold text-amber-400 mb-3 flex items-center gap-2">
                    <span className="text-2xl">⚠️</span>
                    {t('terms.yourResponsibility')}
                  </h3>
                  <p className="text-zinc-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('terms.yourResponsibilityText') }} />
                </div>

                {/* Derechos de DamyxIA */}
                <div className="bg-gradient-to-r from-red-500/10 to-rose-500/10 border border-red-500/30 rounded-xl p-5">
                  <h3 className="text-lg font-semibold text-red-400 mb-3 flex items-center gap-2">
                    <span className="text-2xl">🔒</span>
                    {t('terms.damyxiaRights')}
                  </h3>
                  <div className="text-zinc-300 leading-relaxed space-y-3">
                    <p dangerouslySetInnerHTML={{ __html: t('terms.damyxiaRightsText1') }} />
                    <p dangerouslySetInnerHTML={{ __html: t('terms.damyxiaRightsText2') }} />
                    <p dangerouslySetInnerHTML={{ __html: t('terms.damyxiaRightsText3') }} />
                    <p className="text-amber-300 font-medium" dangerouslySetInnerHTML={{ __html: t('terms.damyxiaRightsText4') }} />
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="border-t border-zinc-800 pt-4">
                  <p className="text-xs text-zinc-500 text-center">
                    {t('terms.disclaimer')}
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-zinc-800 bg-zinc-900/50">
              <button
                onClick={() => setShowRights(false)}
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-lg transition-colors"
                data-testid="accept-rights-btn"
              >
                {t('terms.understood')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Install Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 flex justify-between items-center">
              <div className="flex items-center gap-2"><Download className="w-5 h-5 text-white"/><span className="font-bold text-white">{language === 'es' ? 'Instalar DamyxIA' : 'Install DamyxIA'}</span></div>
              <button onClick={() => setShowInstallModal(false)} className="p-1 hover:bg-white/20 rounded"><X className="w-5 h-5 text-white"/></button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-zinc-400 mb-2">{language === 'es' ? 'Elige tu dispositivo:' : 'Choose device:'}</p>
              
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2"><Monitor className="w-5 h-5 text-blue-400"/><span className="font-medium text-white">Windows / Mac</span></div>
                <ol className="text-sm text-zinc-400 space-y-1 ml-7 list-decimal">
                  <li>{language === 'es' ? 'Abre Chrome o Edge' : 'Open Chrome or Edge'}</li>
                  <li>{language === 'es' ? 'Busca ⊕ en la barra' : 'Find ⊕ in address bar'}</li>
                  <li>{language === 'es' ? 'Clic en "Instalar"' : 'Click "Install"'}</li>
                </ol>
                <p className="text-xs text-emerald-400 mt-2">{language === 'es' ? '📍 Búscala en: Menú Inicio / Launchpad' : '📍 Find it in: Start Menu / Launchpad'}</p>
              </div>

              <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2"><Smartphone className="w-5 h-5 text-green-400"/><span className="font-medium text-white">Android</span></div>
                <ol className="text-sm text-zinc-400 space-y-1 ml-7 list-decimal">
                  <li>{language === 'es' ? 'Abre Chrome' : 'Open Chrome'}</li>
                  <li>{language === 'es' ? 'Menú ⋮ (tres puntos)' : 'Menu ⋮ (three dots)'}</li>
                  <li>{language === 'es' ? '"Añadir a inicio"' : '"Add to Home"'}</li>
                </ol>
                <p className="text-xs text-emerald-400 mt-2">{language === 'es' ? '📱 Aparece en pantalla de inicio' : '📱 Appears on home screen'}</p>
              </div>

              <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2"><Smartphone className="w-5 h-5 text-zinc-400"/><span className="font-medium text-white">iPhone / iPad</span></div>
                <ol className="text-sm text-zinc-400 space-y-1 ml-7 list-decimal">
                  <li>{language === 'es' ? 'Abre Safari' : 'Open Safari'}</li>
                  <li>{language === 'es' ? 'Toca compartir ↑' : 'Tap share ↑'}</li>
                  <li>{language === 'es' ? '"Añadir a inicio"' : '"Add to Home"'}</li>
                </ol>
                <p className="text-xs text-emerald-400 mt-2">{language === 'es' ? '📱 Aparece en pantalla de inicio' : '📱 Appears on home screen'}</p>
              </div>

              <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-3 mt-4">
                <p className="text-sm text-indigo-300 font-medium mb-2">{language === 'es' ? '💡 Información útil' : '💡 Useful info'}</p>
                <ul className="text-xs text-zinc-400 space-y-1">
                  <li>• {language === 'es' ? 'Ocupa solo 5-20 MB' : 'Only 5-20 MB'}</li>
                  <li>• {language === 'es' ? 'Se actualiza automáticamente' : 'Updates automatically'}</li>
                  <li>• {language === 'es' ? 'Funciona parcialmente sin internet' : 'Works partially offline'}</li>
                  <li>• {language === 'es' ? 'Desinstala como cualquier app' : 'Uninstall like any app'}</li>
                </ul>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-zinc-700">
              <button onClick={() => setShowInstallModal(false)} className="w-full py-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-semibold rounded-lg">
                {language === 'es' ? '¡Entendido!' : 'Got it!'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* CUADRO NEGRO — Modales de Transparencia (landing pre-login)   */}
      {/* Replican el contenido oficial de /planes-y-beneficios.        */}
      {/* ============================================================ */}
      {showCanModal && (
        <TransparencyModal
          title={es ? 'Transparencia: Qué Puede DamyxIA Hoy' : 'Transparency: What DamyxIA Can Do Today'}
          onClose={() => setShowCanModal(false)}
          testId="welcome-transparency-can-modal"
        >
          <h3>{es ? 'Creación Visual y Contenido' : 'Visual Creation & Content'}</h3>
          <ul>
            <li>{es ? 'Generar imágenes avanzadas con estilos múltiples.' : 'Generate advanced images with multiple styles.'}</li>
            <li>{es ? 'Crear variaciones y mejoras de imágenes existentes.' : 'Create variations and enhance existing images.'}</li>
            <li>{es ? 'Trabajar en un Workspace visual con historial y snapshots.' : 'Work in a visual Workspace with history and snapshots.'}</li>
          </ul>

          <h3>{es ? 'Asistencia Inteligente' : 'Intelligent Assistance'}</h3>
          <ul>
            <li>{es ? 'Conversar y guiarte paso a paso.' : 'Chat and guide you step by step.'}</li>
            <li>{es ? 'Explicar conceptos y ayudarte a tomar decisiones.' : 'Explain concepts and help you make decisions.'}</li>
            <li>{es ? 'Usar un onboarding real (ORB) para adaptarse a tu uso.' : 'Use real onboarding (ORB) to adapt to your usage.'}</li>
          </ul>

          <h3>{es ? 'Gestión de Proyectos Visuales' : 'Visual Project Management'}</h3>
          <ul>
            <li>{es ? 'Organizar tu trabajo en un espacio de proyectos.' : 'Organize your work in a project space.'}</li>
            <li>{es ? 'Guardar versiones y recuperar estados anteriores.' : 'Save versions and recover previous states.'}</li>
            <li>{es ? 'Mantener tu contenido accesible dentro de los límites del plan.' : 'Keep your content accessible within plan limits.'}</li>
          </ul>

          <h3>{es ? 'Monetización Clara' : 'Clear Monetization'}</h3>
          <ul>
            <li>{es ? 'Plan Free con límites transparentes.' : 'Free Plan with transparent limits.'}</li>
            <li>{es ? 'Plan Premium con acceso ampliado y sin costos ocultos.' : 'Premium Plan with extended access and no hidden costs.'}</li>
            <li>{es ? 'Uso controlado y visible para que sepas siempre dónde estás.' : 'Controlled, visible usage so you always know where you stand.'}</li>
          </ul>

          <p>
            {es
              ? 'DamyxIA hoy es una plataforma estable, profesional y enfocada en creación visual, organización y acompañamiento inteligente.'
              : 'Today DamyxIA is a stable, professional platform focused on visual creation, organization and intelligent support.'}
          </p>
        </TransparencyModal>
      )}

      {showCannotModal && (
        <TransparencyModal
          title={es ? 'Transparencia: Qué NO Puede DamyxIA Hoy' : 'Transparency: What DamyxIA Cannot Do Today'}
          onClose={() => setShowCannotModal(false)}
          testId="welcome-transparency-cannot-modal"
        >
          <h3>{es ? 'Lo que DamyxIA NO hace (todavía)' : 'What DamyxIA does NOT do (yet)'}</h3>
          <ul>
            <li>{es ? 'No crea apps completas (frontend + backend + base de datos).' : 'It does not build complete apps (frontend + backend + database).'}</li>
            <li>{es ? 'No genera proyectos listos para deploy con dominios y hosting.' : 'It does not generate deploy-ready projects with domains and hosting.'}</li>
            <li>{es ? 'No se auto-modifica ni cambia su propio código en producción.' : 'It does not self-modify or change its own production code.'}</li>
            <li>{es ? 'No ejecuta código arbitrario del usuario en servidores propios.' : 'It does not run arbitrary user code on its own servers.'}</li>
            <li>{es ? 'No crea otras instancias de DamyxIA ni "otra IA" independiente.' : 'It does not spawn other DamyxIA instances or an independent "other AI".'}</li>
          </ul>

          <h3>{es ? 'Alcance Actual' : 'Current Scope'}</h3>
          <ul>
            <li>{es ? 'DamyxIA está enfocada en imágenes, contenido y organización visual.' : 'DamyxIA focuses on images, content and visual organization.'}</li>
            <li>{es ? 'No reemplaza a un equipo de desarrollo completo.' : 'It does not replace a full development team.'}</li>
            <li>{es ? 'No garantiza resultados fuera de su ámbito (por ejemplo, deploy, apps móviles, etc.).' : 'It does not guarantee results outside its scope (e.g. deploys, mobile apps).'}</li>
          </ul>

          <h3>{es ? 'Visión Futura (sin promesas mágicas)' : 'Future Vision (no magic promises)'}</h3>
          <ul>
            <li>{es ? 'Explorar generación de proyectos más completos.' : 'Explore more complete project generation.'}</li>
            <li>{es ? 'Explorar integraciones más profundas con código y despliegue.' : 'Explore deeper code and deploy integrations.'}</li>
            <li>{es ? 'Siempre con transparencia, seguridad y sin exagerar sus capacidades.' : 'Always with transparency, security and no exaggerated claims.'}</li>
          </ul>

          <p>
            {es
              ? 'Preferimos ser claros: DamyxIA no es mágica. Es una herramienta potente de creación visual y asistencia, con límites definidos para evitar falsas expectativas y decepciones.'
              : 'We prefer to be clear: DamyxIA is not magic. It is a powerful tool for visual creation and assistance, with defined limits to avoid false expectations and disappointment.'}
          </p>
        </TransparencyModal>
      )}
      </div>
    </div>
  );
};

export default WelcomeScreen;
